// v3E.3 — "Upcoming this week" combined widget for Home dashboard.
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Syringe, CalendarDays, FlaskConical, Repeat, ChevronRight, CalendarClock } from "lucide-react";
import { api } from "../../api/client";

const KIND_META = {
    vaccination: { Icon: Syringe, tint: "from-emerald-500 to-teal-500", label: "Vaccine", chip: "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300" },
    appointment: { Icon: CalendarDays, tint: "from-violet-500 to-indigo-500", label: "Appointment", chip: "bg-violet-50 text-violet-700 dark:bg-violet-500/15 dark:text-violet-300" },
    lab_test: { Icon: FlaskConical, tint: "from-blue-500 to-cyan-500", label: "Lab test", chip: "bg-blue-50 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300" },
    refill: { Icon: Repeat, tint: "from-amber-500 to-rose-500", label: "Refill", chip: "bg-amber-50 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300" },
};

const formatWhen = (iso, kind) => {
    if (!iso) return "—";
    const d = new Date(iso.length === 10 ? `${iso}T00:00:00` : iso);
    if (Number.isNaN(d.getTime())) return iso;
    if (kind === "appointment") {
        return d.toLocaleString("en-IN", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
    }
    return d.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" });
};

const UpcomingThisWeek = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                const { data } = await api.get("/upcoming/this-week", { params: { days: 7 } });
                if (!mounted) return;
                setItems(data.items || []);
            } finally {
                if (mounted) setLoading(false);
            }
        })();
        return () => { mounted = false; };
    }, []);

    if (loading) {
        return <div className="h-44 skeleton rounded-3xl" data-testid="upcoming-skel" />;
    }

    return (
        <section
            data-testid="upcoming-this-week"
            className="overflow-hidden rounded-3xl border border-zinc-100 bg-gradient-to-br from-white via-brand-50/30 to-violet-50/30 shadow-soft dark:border-white/10 dark:from-white/[0.04] dark:via-brand-500/[0.06] dark:to-violet-500/[0.04]"
        >
            <header className="flex items-center justify-between px-5 pt-5">
                <div className="flex items-center gap-2">
                    <span className="grid h-9 w-9 place-items-center rounded-2xl bg-gradient-to-br from-brand-500 to-violet-500 text-white">
                        <CalendarClock className="h-4 w-4" />
                    </span>
                    <div>
                        <p className="label-uppercase text-zinc-500 dark:text-zinc-400">Upcoming</p>
                        <h3 className="text-base font-semibold text-zinc-900 dark:text-zinc-50">This week</h3>
                    </div>
                </div>
                <div className="flex items-center gap-2 text-xs">
                    <Link to="/appointments" className="rounded-full border border-zinc-200 px-3 py-1 text-zinc-600 hover:border-brand-300 hover:text-brand-700 dark:border-white/10 dark:text-zinc-300 dark:hover:border-brand-400/40" data-testid="upcoming-link-appointments">All appointments</Link>
                </div>
            </header>

            {items.length === 0 ? (
                <div className="px-5 pb-5 pt-3" data-testid="upcoming-empty">
                    <p className="rounded-2xl border border-dashed border-zinc-200 bg-white/60 px-4 py-6 text-center text-sm text-zinc-500 dark:border-white/10 dark:bg-white/[0.02] dark:text-zinc-400">
                        Nothing scheduled for the next 7 days. Add a vaccination, appointment or lab reminder to see them here.
                    </p>
                    <div className="mt-3 flex flex-wrap justify-center gap-2 text-xs">
                        <Link to="/vaccinations" className="rounded-full bg-emerald-50 px-3 py-1 font-medium text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300" data-testid="upcoming-add-vacc">+ Vaccination</Link>
                        <Link to="/appointments" className="rounded-full bg-violet-50 px-3 py-1 font-medium text-violet-700 dark:bg-violet-500/15 dark:text-violet-300" data-testid="upcoming-add-appt">+ Appointment</Link>
                        <Link to="/lab-reminders" className="rounded-full bg-blue-50 px-3 py-1 font-medium text-blue-700 dark:bg-blue-500/15 dark:text-blue-300" data-testid="upcoming-add-lab">+ Lab reminder</Link>
                    </div>
                </div>
            ) : (
                <ul className="divide-y divide-zinc-100 px-2 pb-2 pt-2 dark:divide-white/5" data-testid="upcoming-list">
                    {items.slice(0, 5).map((it) => {
                        const meta = KIND_META[it.kind] || KIND_META.appointment;
                        const Icon = meta.Icon;
                        return (
                            <li key={`${it.kind}-${it.id}`} data-testid={`upcoming-item-${it.kind}-${it.id}`}>
                                <Link
                                    to={it.route || "/account"}
                                    className="flex items-center gap-3 rounded-2xl px-3 py-3 transition hover:bg-white dark:hover:bg-white/[0.04]"
                                >
                                    <span className={`grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-gradient-to-br ${meta.tint} text-white`}>
                                        <Icon className="h-4 w-4" />
                                    </span>
                                    <div className="min-w-0 flex-1">
                                        <div className="flex flex-wrap items-center gap-2">
                                            <p className="truncate text-sm font-semibold text-zinc-900 dark:text-zinc-50">{it.title}</p>
                                            <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${meta.chip}`}>{meta.label}</span>
                                        </div>
                                        <p className="mt-0.5 text-xs text-zinc-500 dark:text-zinc-400">{formatWhen(it.when, it.kind)}{it.days_remaining != null ? ` · ${it.days_remaining} day${it.days_remaining === 1 ? "" : "s"} left` : ""}</p>
                                    </div>
                                    <ChevronRight className="h-4 w-4 shrink-0 text-zinc-400" />
                                </Link>
                            </li>
                        );
                    })}
                </ul>
            )}
        </section>
    );
};

export default UpcomingThisWeek;
